<template>
	<div
		class="sidebar-cart"
		:class="{
			'sidebar-cart--inline': isInline,
			'sidebar-cart--editable': isEditable,
		}"
	>
		<div class="sidebar-cart__wrapper">
			<div class="sidebar-cart__cross-sell">
				<CartCrossSell
					v-if="isEditable && !isInline"
				/>
			</div>
			<div class="sidebar-cart__cart">
				<div class="sidebar-cart__inner">
					<h2 class="sidebar-cart__title">
						<span
							class="sidebar-cart__title-text"
							v-html="$t('cart.title')"
						/>
						<button
							class="sidebar-cart__title-close"
							@click="close"
						>
							<SVGClose/>
						</button>
					</h2>
					<div
						class="sidebar-cart__continue"
						v-if="showContinue"
					>
						<Button
							class="sidebar-cart__continue-button"
							@click.prevent="backToShop"
							:text="$t('cart.continueShopping')"
							level="tertiary"
							type="button"
							:arrow="true"
						/>
					</div>
					<div
						class="sidebar-cart__warning"
						v-if="totalItemsWithoutAdditional > 0 && !isCartValidForCheckout"
						v-html="$t('cart.warningMinimumAmount', { price: $options.filters.madpricecent(minCartPrice, 'html')})"
					/>
					<ul class="sidebar-cart__list">
						<component
							:is="itemType(item)"
							v-for="item in items"
							:key="item.id"
							:item="item"
							:locked="lockedItems[item.id]"
							@sub="onSub(item)"
							@add="onAdd(item)"
							@edit="onChildProductModal(item)"
							@remove="onDelete(item)"
							@copy="onCopy(item)"
						/>
						<li
							v-if="totalItemsWithoutAdditional <= 0"
							class="sidebar-cart__list-item sidebar-cart__list-item--empty"
							v-html="$t('cart.warningCartEmpty')"
						/>
					</ul>
				</div>
				<div
					class="sidebar-cart__cross-sell-mobile"
					v-if="cartCrossSell.length && isEditable"
				>
					<Button
						@click.prevent="onCrossSellOpen"
						class="sidebar-cart__cross-sell-mobile-button"
						level="tertiary"
						type="button"
						:text="$t('cart.crossSell.button')"
						arrow
					/>
					<div
						class="sidebar-cart__cross-sell-mobile-hint"
						v-html="$t('cart.crossSell.buttonHint')"
					/>
				</div>
				<SidebarCartAdditionalProducts
					v-if="hasFeature('FEATURE_ADDITIONALPRODUCTS') && additionalItemsAvailable && totalItemsWithoutAdditional"
					:additional-items="additionalItems"
					:additional-items-price="additionalItemsPrice"
					:additional-open="additionalOpen"
					@toggle="onAdditionalToggle"
					@edit="onAdditionalEdit"
				/>
				<div
					v-if="totalItemsWithoutAdditional"
					class="sidebar-cart__summary"
				>
					<div
						class="sidebar-cart__summary-row"
						v-for="discount in discounts"
						:key="discount.name"
					>
						<div class="sidebar-cart__summary-row-info">
							<div
								class="sidebar-cart__summary-row-info-title"
							>
								<span v-html="discount.name"></span>
								<!-- DEBUG: remove coupon -->
							</div>
						</div>
						<button
							v-if="discount.removable !== false"
							class="sidebar-cart__summary-row-remove"
							@click="$store.dispatch('cart/removeCoupon')"
						>
							<SVGClose/>
						</button>
						<div
							class="sidebar-cart__summary-row-price"
							v-html="$options.filters.madpricecent(discount.amount.current, 'short')"
						>
						</div>
					</div>
					<!-- DEBUG ADD COUPON -->
					<!--
					<div
						class="sidebar-cart__summary-row"
						v-if="discounts.length <= 0"
					>
						<div class="sidebar-cart__summary-row-info">
							<button @click="$store.dispatch('cart/addCoupon',{coupon: 'test'})">Add Coupon</button>
						</div>
					</div>
					-->
					<div
						class="sidebar-cart__summary-subtotal"
						v-if="subtotal < total"
					>
						<div class="sidebar-cart__summary-subtotal-title" v-html="$t('cart.subtotal')" />
						<div
							class="sidebar-cart__summary-subtotal-price"
							v-html="$options.filters.madpricecent(subtotal, 'short')"
						/>
					</div>
					<div
						v-if="showDeliveryPrice"
						class="sidebar-cart__summary-row"
					>
						<div class="sidebar-cart__summary-row-info">
							<div class="sidebar-cart__summary-row-info-title" v-html="$t('cart.deliveryFee')" />
							<div
								v-if="minCartFreePrice > 0 && deliveryPrice > 0 && minCartFreePrice - subtotalBeforePromotion > 0"
								class="sidebar-cart__summary-row-info-meta"
								v-html="$t('cart.freeDeliveryDifference', { amount: $options.filters.madpricecent(minCartFreePrice - subtotalBeforePromotion) })"
							/>
						</div>
						<div
							v-if="deliveryPrice"
							v-html="$options.filters.madpricecent(deliveryPrice, 'short')"
							class="sidebar-cart__summary-row-price"
						/>
						<div
							v-else
							class="sidebar-cart__summary-row-meta"
							v-html="$t('cart.deliveryFeeFree')"
						/>
					</div>
					<div
						v-else
						class="sidebar-cart__summary-row"
					>
						<!-- TODO: this probably shouldn't be displayed in checkout-->
						<div class="sidebar-cart__summary-row-info">
							<div class="sidebar-cart__summary-row-info-title" v-html="$t('cart.deliveryFee')" />
						</div>
						<div
							class="sidebar-cart__summary-row-meta"
							v-html="$t('cart.deliveryFeeLater')"
						/>
					</div>
					<div class="sidebar-cart__summary-total">
						<div class="sidebar-cart__summary-total-title" v-html="$t('cart.total')" />
						<div
							class="sidebar-cart__summary-total-price"
							v-html="$options.filters.madpricecent(total, 'short')"
						/>
					</div>
				</div>
				<div class="sidebar-cart__button">
					<Button
						v-show="totalItemsWithoutAdditional > 0"
						@click.prevent="gotoCheckout"
						:disabled="!isCartValidForCheckout"
						class="sidebar-cart__submit"
						:text="$t('cart.orderButton')"
						level="primary"
						type="button"
					/>
				</div>
			</div>
		</div>

		<Modal
			v-if="hasFeature('FEATURE_ADDITIONALPRODUCTS')"
			ref="modal"
			overlay-type="additional-products"
			:html-classes="['hide--header', 'show--modal']"
			soft-close
			@close="onModalClose"
			@softclose="onAdditionalEditSoftClose"
		>
			<OverlayAdditionalProducts
				ref="overlayAdditionalProducts"
				@close="onAdditionalEditClose"
			/>
		</Modal>

		<Modal
			ref="chartChangeModal"
			overlay-type="cart-change"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			@close="onModalClose"
		>
			<!-- TODO: the overlay will be opened in an event and title and message will have to be filled somewhere -->
			<OverlayCartChange
				:title="$t('cart.timeEdit.cartChange.title')"
				:message="$t('cart.timeEdit.cartChange.textAutomatic')"
				:changed-items="cartChangedItems"
				@confirm="onCartChangeClose"
			/>
		</Modal>

		<Modal
			v-if="cartCrossSell.length && isEditable"
			ref="crossSellModal"
			overlay-type="cross-sell"
			:html-classes="['hide--header', 'show--modal']"
			@close="onModalClose"
		>
			<CartCrossSell/>
			<Button
				@click.prevent="onCrossSellClose"
				class="sidebar-cart__cross-sell-mobile-close"
				:text="$t('cart.crossSell.closeButton')"
				level="primary"
				type="button"
			/>
		</Modal>
	</div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import htmlComponent from 'htmlcomponent'
import GenericMixin from '@/mixins/GenericVueMixin';
import OverlayMixin from '@/mixins/OverlayVueMixin';

import OverlayAdditionalProducts from '@/pages/OverlayAdditionalProducts'
import OverlayLocationEdit from '@/pages/OverlayLocationEdit';
import OverlayCartChange from '@/pages/OverlayCartChange';
import SidebarCartItem from '@/components/SidebarCartItem';
import SidebarCartItemConfigurable from '@/components/SidebarCartItemConfigurable';
import SidebarCartAdditionalProducts from '@/components/SidebarCartAdditionalProducts';
import CartCrossSell from '@/components/CartCrossSell';
import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"
import { isMobile } from '@/utils/features';
import cookie from 'cookie_js'

export default {
	mixins: [
		GenericMixin,
		OverlayMixin,
	],
	components: {
		SidebarCartItem,
		SidebarCartItemConfigurable,
		SidebarCartAdditionalProducts,
		CartCrossSell,
		Button,
		OverlayAdditionalProducts,
		Modal,
		OverlayLocationEdit,
		OverlayCartChange,
	},
	props: {
		isEditable: {
			default: true
		},
		isInline: {
			type: Boolean,
			default() {
				return false;
			},
		},
		overlayType: {
			type: String,
			default() {
				return 'sidebar-cart';
			},
		},
	},
	data() {
		return {
			lockedItems: {},
			overlayIgnoreScrollPos: true,
			additionalOpen: false,
			editItem: null,
			editItemSelectedIds: [],
			overlaysAllowed: [
				'additional-products',
				'childproducts',
				'location-change',
				'cart-change',
				'cross-sell',
			],
			modalCloseParent: false,
			crossSellDelay: 2000,
			crossSellTimeout: null,
			crossSellCookie: 'mscrosssell',
		};
	},
	computed: {
		...mapGetters("cart", [
			"cartDeliveryTime",
			"cartDeliveryTimeslot",
			"total",
			"subtotal",
			"subtotalBeforePromotion",
			"totalItemsWithoutAdditional",
			"isCartValidForCheckout",
			"discounts",
			"deliveryPrice",
			"deliveryTime",
			"minCartPrice",
			"minCartFreePrice",
			"shippingMethod",
			"shopLocation",
			"cartChangedItems",
			"cartOrderNow",
			"cartTip",
			"showContinue",
			"cartCrossSell",
		]),
		...mapGetters("additionalproducts", [
			"items",
			"additionalItems",
			"additionalItemsPrice",
			"additionalItemsAvailable",
		]),
		...mapGetters("childproducts", [
			"childProducts"
		]),
		showDeliveryPrice() {
			return this.shippingMethod ||
				this.deliveryPrice > 0 ||
				(
					this.minCartFreePrice &&
					this.minCartFreePrice < this.subtotal
				)
		}
	},
	methods: {
		backToShop() {
			// TODO: this should be done with a proper redirect
			this.$store.dispatch('redirect', {name: 'shop'})
		},
		itemType(item) {
			return item.isEditable ? 'SidebarCartItemConfigurable' : 'SidebarCartItem';
		},
		async onAdd(item) {
			Vue.set(this.lockedItems, item.id, true);
			try {
				await this.$store.dispatch("cart/updateItem", { identifier: item.id, quantity: item.quantity + 1 })
			}
			catch(err) {
				this.$store.dispatch('dialog/showDialog', {
					title: this.$t('forms.errors.error'),
					message: this.$t('forms.errors.generic'),
					confirm: 'OK',
				});
			}
			Vue.set(this.lockedItems, item.id, false);
		},
		async onSub(item) {
			Vue.set(this.lockedItems, item.id, true);
			try {
				await this.$store.dispatch("cart/updateItem", { identifier: item.id, quantity: item.quantity - 1 })
			}
			catch(err) {
				this.$store.dispatch('dialog/showDialog', {
					title: this.$t('forms.errors.error'),
					message: this.$t('forms.errors.generic'),
					confirm: 'OK',
				});
			}
			Vue.set(this.lockedItems, item.id, false);
		},
		async onDelete(item) {
			Vue.set(this.lockedItems, item.id, true);
			try {
				await this.$store.dispatch("cart/updateItem", { identifier: item.id, quantity: 0 })
			}
			catch(err) {
				this.$store.dispatch('dialog/showDialog', {
					title: this.$t('forms.errors.error'),
					message: this.$t('forms.errors.generic'),
					confirm: 'OK',
				});
			}
			Vue.set(this.lockedItems, item.id, false);
		},
		close() {
			this.closeOverlay();
		},
		gotoCheckout() {
			if (this.totalItemsWithoutAdditional > 0) {
				clearTimeout(this.crossSellTimeout);
				this.$store.dispatch('redirect', {name: "checkout"})
			}
		},
		onModalClose() {
			if (this.modalCloseParent || this.isInline && !isMobile()) {
				this.closeOverlay();
				this.modalCloseParent = false;
			}
		},
		onAdditionalEdit() {
			this.$refs.modal.open();
		},
		onAdditionalEditSoftClose() {
			this.$refs.overlayAdditionalProducts.onClose();
		},
		onAdditionalEditClose() {
			this.$refs.modal.close();
		},
		onAdditionalToggle() {
			this.additionalOpen = !this.additionalOpen;
		},
		onCrossSellOpen() {
			clearTimeout(this.crossSellTimeout);
			this.$refs.crossSellModal.open();
		},
		onCrossSellClose() {
			clearTimeout(this.crossSellTimeout);
			this.$refs.crossSellModal.close();
		},
		async onCopy(item) {
			try {
				await this.$store.dispatch('cart/copyItem', { identifier: item.id })
			}
			catch(err) {
				this.$store.dispatch('dialog/showDialog', {
					title: this.$t('forms.errors.error'),
					message: this.$t('forms.errors.generic'),
					confirm: 'OK',
				});
			}
		},
		onNotifyProductAdded(item) {
			if (item.isEditable && !item.isConfigurable) {
				this.onChildProductModal(item)
			}
		},
		onChildProductModal(editItem) {
			htmlComponent.send('overlay:open', {type: 'overlay-childproducts', item: editItem, selectedCodes: editItem.children.map(v => v.code)});
		},
		onCartChangeClose() {
			this.$refs.chartChangeModal.close();
		},
		async beforeOverlayOpen(data) {
			if (this.isInline && !isMobile()) {
				this.closeOverlay();
				// this error prevents the overlay from applying classes to html when inline and on desktop
				throw new Error();
			}
		},
		afterOverlayOpen(data) {
			if ((isMobile() || this.isInline) && this.isEditable) {
				const hasCookie = cookie.get(this.crossSellCookie);

				if (!hasCookie && this.cartCrossSell.length) {
					this.crossSellTimeout = setTimeout(() => {
						this.onCrossSellOpen();
						cookie.set(this.crossSellCookie, 'true', { path: '/' });
					}, this.crossSellDelay);
				}
			}
		},
		beforeOverlayClose(data) {
			clearTimeout(this.crossSellTimeout);
		},
	}
}
</script>

<style>

</style>
